<template>
  <PVBreadcrumb
    :model="model"
    :pt="{
      separatorIcon: { height: 8, width: 8 },
    }"
  >
    <template #separator>
      <i class="material-symbols-rounded text-base">chevron_right</i>
    </template>

    <template #item="{ item }">
      <button
        v-if="item.overflow"
        type="button"
        class="c-breadcrumb-overflow"
        :class="itemClass"
        :aria-label="t('breadcrumb.showHiddenItems')"
        aria-haspopup="menu"
        :aria-expanded="overflowOpen"
        @click="toggleOverflow"
      >
        &hellip;
      </button>
      <router-link v-else-if="item.to" :to="item.to">
        <!-- @vue-expect-error FIXME: 'MenuItem' not assignable to 'BreadcrumbItem' -->
        <BreadcrumbItem :item="item" :class="itemClass" />
      </router-link>
      <!-- @vue-expect-error FIXME: 'MenuItem' not assignable to 'BreadcrumbItem' -->
      <BreadcrumbItem v-else :item="item" :class="itemClass" @click="item.command" />
    </template>
  </PVBreadcrumb>

  <Menu
    v-if="hiddenItems.length"
    ref="overflowMenu"
    :model="hiddenItems"
    :popup="true"
    class="c-breadcrumb-overflow-menu"
    @show="overflowOpen = true"
    @hide="overflowOpen = false"
  />
</template>

<script setup lang="ts">
import { computed, ref, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import type { BreadcrumbItem as BreadcrumbItemType } from '@centric-os/types';
import PVBreadcrumb from 'primevue/breadcrumb';
import { Menu } from '../menu';
import BreadcrumbItem from './BreadcrumbItem.vue';

// Pantry breadcrumb spec: show the full trail up to five items; at six or more,
// collapse everything between the root and the last two into an overflow crumb
// that opens a popover listing the hidden levels.
const OVERFLOW_THRESHOLD = 6;

const props = defineProps({
  items: Array as PropType<BreadcrumbItemType[]>,
  itemClass: String,
});

const { t } = useI18n();

const overflowMenu = ref<InstanceType<typeof Menu>>();
const overflowOpen = ref<boolean>(false);

const hasOverflow = computed<boolean>(() => (props.items?.length ?? 0) >= OVERFLOW_THRESHOLD);

const hiddenItems = computed<BreadcrumbItemType[]>(() =>
  hasOverflow.value ? props.items.slice(1, -2) : [],
);

const model = computed<BreadcrumbItemType[]>(() => {
  if (!hasOverflow.value) return props.items ?? [];
  const [root] = props.items;
  return [root, { label: '…', overflow: true }, ...props.items.slice(-2)];
});

const toggleOverflow = (event: Event): void => {
  overflowMenu.value?.toggle(event);
};
</script>

<script lang="ts">
export default { name: 'Breadcrumb', inheritAttrs: false };
</script>
